import { AuthApi } from '@quadminds/client-apis/auth';
import { IAuthMeV2OutputDto, Permission } from '@quadminds/shared/auth-system';
import { clearTokensFromStorage } from '@quadminds/shared/utils';
import axios, { AxiosError } from 'axios';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

type CheckUserOptions = {
    /**
     * User to evaluate permissions
     */
    user?: IAuthMeV2OutputDto;

    /**
     * When passed down, user is evaluated to check if has required permissions
     */
    validatePermissions: Permission[];

    /**
     * Auth version strategy to get authentication.
     * When V1 is selected, user's session cookie is used to fetch access tokens
     * When V2 is selected, available refresh token is used to fetch new access tokens
     * Defaults to version 1
     */
    authVersion?: number;
    redirectUrl?: string;
};

const defaultOptions = { authVersion: 1, validatePermissions: [] };

/**
 *  Hook used to check if user has provided permissions
 *  If no user provided, attempts to get his info through token or session
 */
export const useCheckUserPermissions = (hookOptions: CheckUserOptions = defaultOptions) => {
    const authApi = useMemo(() => new AuthApi(), []);
    const didMount = useRef(false);

    const [user, setUser] = useState<IAuthMeV2OutputDto | undefined>(hookOptions.user);
    const [isLoading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<AxiosError | null>(null);

    const [hasPermissions, setHasPermissions] = useState<boolean>(true);
    const [options] = useState<CheckUserOptions>({
        ...defaultOptions,
        ...hookOptions,
    });

    const getUserInfo = useCallback(async () => {
        try {
            const userInfo = await authApi.getUserInfo();
            return userInfo;
        } catch (error: unknown) {
            if (axios.isAxiosError(error)) {
                setError(error);
            }
            return undefined;
        }
    }, [authApi]);

    const checkSession = useCallback(async () => {
        try {
            const v1Session = await authApi.checkSession();
            return v1Session;
        } catch (error: unknown) {
            if (axios.isAxiosError(error)) {
                setError(error);
            }
            return undefined;
        }
    }, [authApi]);

    const refreshToken = useCallback(async () => {
        try {
            const v2Session = await authApi.refreshToken();
            return v2Session;
        } catch (error: unknown) {
            if (axios.isAxiosError(error)) {
                setError(error);
            }
            return undefined;
        }
    }, [authApi]);

    /* Returns user if provided in hook options or fetches his data according to auth strategy version */
    const getUser = useCallback(async () => {
        try {
            if (user) return user;

            const hasToRefresh = options.authVersion === 2;
            const urlParams = new URLSearchParams(window.location.search);
            const tokenFromUrl = urlParams.get('token');

            // For v2 (stork/tdc): first attempt with the existing token to avoid an unnecessary
            // refresh round-trip that clears localStorage before we can use the refresh token.
            if (hasToRefresh && !tokenFromUrl) {
                const existingUser = await authApi.getUserInfo().catch(() => undefined);
                if (existingUser) {
                    setUser(existingUser);
                    return existingUser;
                }
            }

            clearTokensFromStorage();

            const auth = hasToRefresh ? await refreshToken() : await checkSession();

            if (!auth && !tokenFromUrl) {
                window.location.href = options.redirectUrl || '/';
                return undefined;
            }

            localStorage.setItem('token', (auth?.accessToken || tokenFromUrl) as string);

            const userResponse = await getUserInfo();

            setUser(userResponse);
            return userResponse;
        } catch (error: unknown) {
            if (axios.isAxiosError(error)) {
                setError(error);
            }
            return undefined;
        }
    }, [user, options.authVersion, options.redirectUrl, refreshToken, checkSession, getUserInfo, authApi]);

    const validatePermissions = useCallback((permissions?: Permission[], user?: IAuthMeV2OutputDto) => {
        if (!permissions) return true;

        const hasAllPermissions = permissions?.every((permission) => user?.permissions.includes(permission));
        return hasAllPermissions;
    }, []);

    // Get and validate upon mount
    useEffect(() => {
        if (didMount.current) return;

        const checkUserAsync = async () => {
            setLoading(true);
            const user = await getUser();
            setLoading(false);
            const hasAllPermissions = validatePermissions(options.validatePermissions, user);
            setHasPermissions(hasAllPermissions);
        };

        checkUserAsync();
        didMount.current = true;
    }, [options.validatePermissions, validatePermissions, getUser]);

    return { isLoading, error, hasPermissions, user };
};
